import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr, pct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export default function WeeklyReport() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const workspace_id = params.get("workspace_id");
  const date_from = params.get("date_from");
  const date_to = params.get("date_to");
  const brand = params.get("brand") || "";
  const [data, setData] = useState(null);
  const [ws, setWs] = useState({ name: brand });

  useEffect(() => {
    (async () => {
      if (!workspace_id) return;
      const [summary, audit, allWs] = await Promise.all([
        api.get("/dashboard/summary", { params: { workspace_id, date_from, date_to } }),
        api.get("/dashboard/audit", { params: { workspace_id, date_from, date_to } }),
        api.get("/workspaces"),
      ]);
      setData({ summary: summary.data, audit: audit.data });
      const found = allWs.data.find((w) => w.id === workspace_id);
      if (found) setWs(found);
    })();
  }, [workspace_id, date_from, date_to]);

  useEffect(() => {
    if (data) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (!data) return <div className="min-h-screen bg-white text-black p-12 flex items-center justify-center">Preparing report…</div>;

  const k = data.summary.kpis;
  const bleeding = data.audit.flags.bleeding_skus.items || [];
  const drivers = data.audit.top_drivers;

  const fmtLong = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const dateRangeLabel = `${new Date(date_from).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} → ${new Date(date_to).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`;
  const dateRangeLong = `${fmtLong(date_from)} — ${fmtLong(date_to)}`;

  return (
    <div className="report-shell min-h-screen bg-white text-black">
      {/* Screen-only toolbar */}
      <div className="print-hide sticky top-0 border-b border-neutral-200 bg-white px-8 py-3 flex items-center justify-between z-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm hover:underline" data-testid="report-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="text-xs text-neutral-500">Preview · press ⌘/Ctrl + P to save as PDF</div>
        <Button onClick={() => window.print()} className="rounded-none bg-black text-white hover:bg-neutral-800" data-testid="report-print">
          <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
        </Button>
      </div>

      {/* Page */}
      <div className="report-page mx-auto bg-white text-black">
        {/* Header */}
        <div className="border-b-2 border-black pb-4 mb-8 flex items-start justify-between">
          <div>
            <div className="overline">MarginOps by Ask · Financial Audit</div>
            <h1 className="font-display text-3xl font-bold mt-1 tracking-tight" data-testid="report-title">Executive Profitability Memo</h1>
            <div className="text-xs text-neutral-600 font-mono mt-1" data-testid="report-subtitle">Report for {dateRangeLong}</div>
          </div>
          <div className="text-right">
            <div className="overline">Prepared for</div>
            <div className="font-display text-lg font-semibold mt-1">{ws.name}</div>
            <div className="text-xs text-neutral-600 font-mono">{ws.brand_name || ""}</div>
            <div className="text-xs text-neutral-600 font-mono mt-1" data-testid="report-daterange">{dateRangeLabel}</div>
          </div>
        </div>

        {/* Headline KPIs */}
        <div className="grid grid-cols-4 border border-neutral-300 mb-6" data-testid="report-kpis">
          <div className="p-4 border-r border-neutral-300">
            <div className="overline text-neutral-500">Net Cash Collected</div>
            <div className="font-mono text-2xl font-semibold mt-1">{inr(k.net_revenue, { decimals: 0 })}</div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">{k.total_orders} orders</div>
          </div>
          <div className="p-4 border-r border-neutral-300">
            <div className="overline text-neutral-500">Net Margin %</div>
            <div className={`font-mono text-2xl font-semibold mt-1 ${k.net_margin_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>{pct(k.net_margin_pct, 2)}</div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">Real Net / Net Rev</div>
          </div>
          <div className="p-4 border-r border-neutral-300">
            <div className="overline text-neutral-500">Blended ROAS</div>
            <div className={`font-mono text-2xl font-semibold mt-1 ${k.blended_roas >= 2.5 ? "text-emerald-700" : "text-red-700"}`}>{k.blended_roas.toFixed(2)}x</div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">Net Rev / Ad Spend</div>
          </div>
          <div className="p-4">
            <div className="overline text-neutral-500">Total Ad Spend</div>
            <div className="font-mono text-2xl font-semibold mt-1">{inr(k.total_ad_spend, { decimals: 0 })}</div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">Meta + Google</div>
          </div>
        </div>

        {/* P&L Waterfall */}
        <div className="mb-6" data-testid="report-waterfall">
          <div className="overline text-neutral-500 mb-2">P&L Waterfall</div>
          <table className="w-full text-sm border border-neutral-300">
            <tbody className="font-mono">
              <tr className="border-b border-neutral-200"><td className="p-2">Gross Revenue</td><td className="p-2 text-right">{inr(k.gross_revenue, { decimals: 0 })}</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) Discounts &amp; Coupons</td><td className="p-2 text-right">({inr(k.discounts, { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) Returns / Refunds</td><td className="p-2 text-right">({inr(k.refunded_amount || 0, { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 font-semibold bg-neutral-50"><td className="p-2">Net Revenue</td><td className="p-2 text-right">{inr(k.net_revenue, { decimals: 0 })}</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) GST (stripped)</td><td className="p-2 text-right">({inr(Math.max(0, (k.net_revenue || 0) - (k.net_revenue_ex_gst || 0)), { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 font-semibold bg-neutral-50"><td className="p-2">Net Revenue (Ex-GST)</td><td className="p-2 text-right">{inr(k.net_revenue_ex_gst, { decimals: 0 })}</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) COGS</td><td className="p-2 text-right">({inr(k.total_cogs, { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) Primary Shipping</td><td className="p-2 text-right">({inr(k.primary_shipping || 0, { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 font-semibold bg-neutral-50"><td className="p-2">Contribution Margin</td><td className="p-2 text-right">{inr(k.contribution_margin, { decimals: 0 })}</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) Ad Spend</td><td className="p-2 text-right">({inr(k.total_ad_spend, { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) Channel Commissions</td><td className="p-2 text-right">({inr(k.total_marketplace_fees || 0, { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) PG Fees (incl. 18% GST)</td><td className="p-2 text-right">({inr(k.pg_cost, { decimals: 0 })})</td></tr>
              <tr className="border-b border-neutral-200 text-red-700"><td className="p-2">(–) RTO Freight Loss</td><td className="p-2 text-right">({inr(k.rto_freight_loss || 0, { decimals: 0 })})</td></tr>
              <tr className={`font-bold border-t-2 border-black ${k.real_net_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                <td className="p-2">Real Net Profit</td>
                <td className="p-2 text-right">{inr(k.real_net_profit, { decimals: 0 })}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Fulfillment Health · RTO Leakage + COD Mix */}
        <div className="grid grid-cols-4 border border-neutral-300 mb-6" data-testid="report-fulfillment">
          <div className="p-4 border-r border-neutral-300">
            <div className="overline text-neutral-500">RTO Leakage</div>
            <div className="font-mono text-2xl font-semibold mt-1 text-red-700">{inr(k.rto_leakage || 0, { decimals: 0 })}</div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">Fwd {inr(k.rto_forward_shipping || 0, { decimals: 0 })} + Rev {inr(k.reverse_shipping || 0, { decimals: 0 })}</div>
          </div>
          <div className="p-4 border-r border-neutral-300">
            <div className="overline text-neutral-500">RTO Freight Loss</div>
            <div className="font-mono text-2xl font-semibold mt-1 text-red-700">{inr(k.rto_freight_loss || 0, { decimals: 0 })}</div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">Rev {inr(k.reverse_shipping || 0, { decimals: 0 })} + Pen {inr(k.rto_fee || 0, { decimals: 0 })} + Pack {inr(k.packaging_returned || 0, { decimals: 0 })}</div>
          </div>
          <div className="p-4 border-r border-neutral-300">
            <div className="overline text-neutral-500">COD Mix</div>
            <div className={`font-mono text-2xl font-semibold mt-1 ${(k.cod_mix_pct || 0) > 60 ? "text-red-700" : (k.cod_mix_pct || 0) < 30 ? "text-emerald-700" : "text-neutral-800"}`}>
              {pct(k.cod_mix_pct || 0, 2)}
            </div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">{k.cod_orders || 0} COD · {k.prepaid_orders || 0} Prepaid</div>
          </div>
          <div className="p-4">
            <div className="overline text-neutral-500">Channel Commissions</div>
            <div className="font-mono text-2xl font-semibold mt-1 text-red-700">{inr(k.total_marketplace_fees || 0, { decimals: 0 })}</div>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">Marketplace + platform fees</div>
          </div>
        </div>

        {/* Margin Matrix · Prepaid vs COD */}
        {k.margin_matrix && (
          <div className="mb-6" data-testid="report-margin-matrix">
            <div className="overline text-neutral-500 mb-2">Margin Matrix · Prepaid vs COD</div>
            <table className="w-full text-sm border border-neutral-300">
              <thead>
                <tr className="border-b border-neutral-300 bg-neutral-50">
                  <th className="p-2 text-left overline text-neutral-500">Metric</th>
                  <th className="p-2 text-right overline text-neutral-500">Prepaid</th>
                  <th className="p-2 text-right overline text-neutral-500">COD</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {(() => {
                  const p = k.margin_matrix.prepaid || {};
                  const c = k.margin_matrix.cod || {};
                  const rows = [
                    ["Orders", (p.orders || 0).toLocaleString("en-IN"), (c.orders || 0).toLocaleString("en-IN")],
                    ["Units", (p.units || 0).toLocaleString("en-IN"), (c.units || 0).toLocaleString("en-IN")],
                    ["Net Rev (Ex-GST)", inr(p.net_revenue_ex_gst || 0, { decimals: 0 }), inr(c.net_revenue_ex_gst || 0, { decimals: 0 })],
                    ["(–) COGS", `(${inr(p.cogs || 0, { decimals: 0 })})`, `(${inr(c.cogs || 0, { decimals: 0 })})`],
                    ["(–) Shipping", `(${inr(p.shipping || 0, { decimals: 0 })})`, `(${inr(c.shipping || 0, { decimals: 0 })})`],
                    ["(–) PG Fees", `(${inr(p.pg_cost || 0, { decimals: 0 })})`, `(${inr(c.pg_cost || 0, { decimals: 0 })})`],
                    ["(–) Channel Fees", `(${inr(p.marketplace_fees || 0, { decimals: 0 })})`, `(${inr(c.marketplace_fees || 0, { decimals: 0 })})`],
                    ["(–) Ad Spend (alloc)", `(${inr(p.ad_spend_alloc || 0, { decimals: 0 })})`, `(${inr(c.ad_spend_alloc || 0, { decimals: 0 })})`],
                  ];
                  return rows.map(([label, pv, cv]) => (
                    <tr key={label} className="border-b border-neutral-200">
                      <td className="p-2">{label}</td>
                      <td className="p-2 text-right">{pv}</td>
                      <td className="p-2 text-right">{cv}</td>
                    </tr>
                  ));
                })()}
                <tr className="border-t-2 border-black font-bold">
                  <td className="p-2">Real Net Profit</td>
                  <td className={`p-2 text-right ${(k.margin_matrix.prepaid?.real_net_profit || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {inr(k.margin_matrix.prepaid?.real_net_profit || 0, { decimals: 0 })}
                  </td>
                  <td className={`p-2 text-right ${(k.margin_matrix.cod?.real_net_profit || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {inr(k.margin_matrix.cod?.real_net_profit || 0, { decimals: 0 })}
                  </td>
                </tr>
                <tr className="border-b border-neutral-200 font-semibold">
                  <td className="p-2">Net Margin %</td>
                  <td className={`p-2 text-right ${(k.margin_matrix.prepaid?.net_margin_pct || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {pct(k.margin_matrix.prepaid?.net_margin_pct || 0, 2)}
                  </td>
                  <td className={`p-2 text-right ${(k.margin_matrix.cod?.net_margin_pct || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {pct(k.margin_matrix.cod?.net_margin_pct || 0, 2)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="text-[10px] text-neutral-500 font-mono mt-1">Ad spend allocated to each bucket proportional to Net Rev (Ex-GST) share.</div>
          </div>
        )}

        {/* Two-col */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Action items */}
          <div data-testid="report-actions">
            <div className="overline text-neutral-500 mb-2">Key Action Items</div>
            <div className="border border-neutral-300 p-4 text-sm space-y-2">
              {data.audit.action_items.map((a, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="font-mono text-neutral-400 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span>{a}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bleeding SKUs */}
          <div data-testid="report-bleeding">
            <div className="overline text-neutral-500 mb-2">Bleeding SKU Highlights</div>
            <div className="border border-neutral-300">
              {bleeding.length === 0 && <div className="p-4 text-sm text-neutral-500">No SKUs bleeding cash for this period.</div>}
              {bleeding.slice(0, 5).map((s) => (
                <div key={s.sku_id} className="p-3 border-b border-neutral-200 last:border-b-0 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-xs text-neutral-500">{s.sku_code}</div>
                    <div className="text-sm">{s.product_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold text-red-700">{inr(s.unit_net_profit, { decimals: 2 })}</div>
                    <div className="font-mono text-[10px] text-neutral-500">{pct(s.unit_margin_pct, 1)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top drivers strip */}
        <div className="mb-6">
          <div className="overline text-neutral-500 mb-2">Top 3 Profit Drivers</div>
          <div className="grid grid-cols-3 border border-neutral-300">
            {drivers.map((s) => (
              <div key={s.sku_id} className="p-4 border-r border-neutral-300 last:border-r-0">
                <div className="font-mono text-xs text-neutral-500">{s.sku_code}</div>
                <div className="text-sm font-medium truncate">{s.product_name}</div>
                <div className="font-mono text-lg font-semibold text-emerald-700 mt-2">{inr(s.unit_net_profit, { decimals: 2 })}</div>
                <div className="font-mono text-[10px] text-neutral-500">Unit margin {pct(s.unit_margin_pct, 1)}</div>
              </div>
            ))}
            {drivers.length === 0 && <div className="p-4 text-sm text-neutral-500 col-span-3">No SKU data.</div>}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-300 pt-4 flex items-end justify-between text-[10px] font-mono text-neutral-500">
          <div>Generated {new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · Confidential · MarginOps by Ask</div>
          <div>Discount {pct(k.discount_pct, 2)} · Units {k.total_units_sold.toLocaleString("en-IN")}</div>
        </div>
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 14mm 12mm; }
        .report-page { max-width: 210mm; padding: 24px 32px; }
        @media print {
          .print-hide { display: none !important; }
          .report-page { padding: 0; max-width: 100%; }
          .report-shell { background: white !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
